import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as adminService from "@/shared/api/services/adminService";
import * as transactionService from "@/shared/api/services/transactionService";
import * as qrCodeService from "@/shared/api/services/qrCodeService"
import * as providerService from "@/shared/api/services/providerService";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  ArrowLeft,
  Pencil,
  Building2,
  TrendingUp,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  Clock,
  Activity,
  Key,
  Eye,
  EyeOff,
  Copy,
  Check,
  Percent,
  Webhook,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Users,
  Zap,
} from "lucide-react";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import DrillDownBreadcrumb from "@/shared/components/admin/DrillDownBreadcrumb";

// This page uses the default admin layout, so no custom layout wrapper needed

const statusConfig = {
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  suspended: { icon: AlertTriangle, color: "bg-red-100 text-red-700", label: "Suspended" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
  terminated: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "Terminated" },
};

export default function AdminMerchantDetail() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const merchantId = urlParams.get("id");

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);
  const [txFilters, setTxFilters] = useState({ search: "", status: "all", type: "all" });
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Fetch merchant details
  const { data: merchantData, isLoading: merchantLoading } = useQuery({
    queryKey: ["merchant-detail", merchantId],
    queryFn: async () => {
      if (!merchantId) return null;
      return adminService.getMerchantById(merchantId);
    },
    enabled: !!merchantId,
  });

  // Fetch all transactions and filter by merchant
  const { data: allTransactions = [], isLoading: txLoading } = useQuery({
    queryKey: ["all-transactions"],
    queryFn: async () => transactionService.getAllTransactions(),
  });

  // Fetch all QR codes (admin); filter by merchantId (getQRCodesByMerchantId not in schema yet)
  const { data: qrCodes = [], isLoading: isLoadingQRCodes } = useQuery({
    queryKey: ["qr-codes", merchantId],
    queryFn: () => qrCodeService.getQRCodesByMerchantId(merchantId),
  });

  // Filter transactions by merchant ID and normalize status values
  const transactions = useMemo(() => {
    if (!merchantId || !allTransactions) return [];
    return allTransactions
      .filter(tx => tx.merchant_id === merchantId)
      .map(tx => ({
        ...tx,
        // Normalize status from uppercase enum to lowercase
        status: tx.status?.toLowerCase() || tx.status,
        // Normalize type from uppercase enum to lowercase
        type: tx.type?.toLowerCase() || tx.type,
      }));
  }, [allTransactions, merchantId]);

  // Combine transactions + QR codes (by merchant) as in MerchantTransactions
  const combinedData = useMemo(() => {
    const formattedQRCodes = (qrCodes || []).map((qr) => ({
      id: qr.id,
      transaction_id: qr.payment_id,
      merchant_ref: qr.reference,
      type: "deposit",
      status: qr.status === "paid" || qr.status === "completed" ? "success" : (qr.status || "pending").toLowerCase(),
      amount: qr.amount,
      provider_display: qr.client_bank_name || "—",
      currency: qr.currency,
      created_date: qr.createdAt,
      processed_at: qr.updatedAt,
      completed_at: qr.updatedAt,
      provider_code: "QR Code",
      payment_method: "PROMPTPAY_QR",
      is_qr_code: true,
    }));
    return [...transactions, ...formattedQRCodes].sort(
      (a, b) => new Date(b.created_date) - new Date(a.created_date)
    );
  }, [transactions, qrCodes]);

  // Fetch supporting data
  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: async () => adminService.getAllResellers(),
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: async () => adminService.getAllEngines(),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => providerService.getAllProviders(),
  });

  const { data: allMerchants = [] } = useQuery({
    queryKey: ["all-merchants"],
    queryFn: async () => adminService.getAllMerchants(),
  });

  // Map backend fields to frontend expectations
  const merchant = useMemo(() => {
    if (!merchantData) return null;

    // Normalize status to lowercase
    const normalizedStatus = merchantData.status?.toLowerCase() || 'pending';

    // Determine fee value and type
    const feeValue = merchantData.percentageFee || merchantData.depositCommission || merchantData.withdrawalCommission || 0;
    const feeType = merchantData.percentageFee ? 'percentage' : 'fixed';

    // Find parent reseller and engine
    const parentReseller = merchantData.createdByResellerId
      ? resellers.find(r => r.id === merchantData.createdByResellerId)
      : null;
    const parentEngine = merchantData.createdByEngineId
      ? engines.find(e => e.id === merchantData.createdByEngineId)
      : null;

    return {
      ...merchantData,
      // Map fields
      contact_email: merchantData.email,
      created_date: merchantData.createdAt,
      status: normalizedStatus,
      // Fee fields
      fee_value: feeValue,
      fee_type: feeType,
      // Default values for missing fields
      settlement_period: "T1",
      rate_limit: 100,
      deposit_enabled: true,
      withdrawal_enabled: true,
      min_deposit: null,
      max_deposit: null,
      min_withdrawal: null,
      max_withdrawal: null,
      callback_url: merchantData.callbackUrl || null,
      webhook_url: merchantData.webhookUrl || null,
      ip_whitelist: [],
      // Parent relationships
      parentReseller,
      parentEngine,
    };
  }, [merchantData, resellers, engines]);

  if (merchantLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-slate-500">{t ? t("loading") : "Loading..."}</p>
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-slate-500">
          {t ? t("noMerchantsFound") : "Merchant not found"}
        </p>
      </div>
    );
  }

  const status = statusConfig[merchant.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  // Get parent reseller and engine from mapped merchant data
  const parentReseller = merchant.parentReseller;
  const parentEngine = merchant.parentEngine;

  // Calculate KPIs
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);

  const todayTxs = transactions.filter(t => {
    const txDate = new Date(t.created_date);
    return txDate >= todayStart && txDate <= todayEnd;
  });

  const todayVolume = todayTxs.filter(t => t.status === "success").reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalVolume = transactions.filter(t => t.status === "success").reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalDeposits = transactions.filter(t => t.type === "deposit" && t.status === "success").reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalWithdrawals = transactions.filter(t => t.type === "withdrawal" && t.status === "success").reduce((sum, t) => sum + (t.amount || 0), 0);
  const successCount = transactions.filter(t => t.status === "success").length;
  const successRate = transactions.length > 0 ? ((successCount / transactions.length) * 100).toFixed(1) : 0;
  const avgDepositAmount = transactions.filter(t => t.type === "deposit" && t.status === "success").length > 0
    ? (totalDeposits / transactions.filter(t => t.type === "deposit" && t.status === "success").length).toFixed(0)
    : 0;

  // Chart data
  const chartData = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);
    const dayTxs = transactions.filter(t =>
      new Date(t.created_date).toDateString() === date.toDateString() && t.status === "success"
    );
    return {
      date: format(date, "MMM d"),
      deposits: dayTxs.filter(t => t.type === "deposit").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
      withdrawals: dayTxs.filter(t => t.type === "withdrawal").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
    };
  });

  // Filter combined transactions + QR codes
  const filteredTxs = combinedData.filter(tx => {
    if (txFilters.search) {
      const search = txFilters.search.toLowerCase();
      if (!tx.transaction_id?.toLowerCase().includes(search) && !tx.merchant_ref?.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (txFilters.status !== "all" && tx.status !== txFilters.status) return false;
    if (txFilters.type !== "all" && tx.type !== txFilters.type) return false;
    return true;
  });

  const paginatedTxs = filteredTxs.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredTxs.length / pageSize);

  const copyKey = (key, type) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(type);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link to={createPageUrl("Merchants")}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center">
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">{merchant.name}</h1>
                <Badge className={`${status.color} gap-1 border-0`}>
                  <StatusIcon className="h-3 w-3" />
                  {t ? (t(merchant.status) !== merchant.status ? t(merchant.status) : status.label) : status.label}
                </Badge>
              </div>
              <p className="text-slate-500">{merchant.code} • {merchant.contact_email}</p>
            </div>
          </div>
        </div>
        <Link to={createPageUrl("Merchants")}>
          <Button className="gap-2">
            <Pencil className="h-4 w-4" />
            {t ? t("editMerchant") : "Edit Merchant"}
          </Button>
        </Link>
      </div>

      {/* Drill Down Breadcrumb */}
      <DrillDownBreadcrumb engine={parentEngine} reseller={parentReseller} merchant={merchant} />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { title: t ? t("todaysVolume") : "Today's Volume", value: `฿${todayVolume.toLocaleString()}`, icon: TrendingUp, color: "bg-blue-100 text-blue-600" },
          { title: t ? t("totalVolume") : "Total Volume", value: `฿${totalVolume.toLocaleString()}`, icon: Activity, color: "bg-emerald-100 text-emerald-600" },
          { title: t ? t("successRate") : "Success Rate", value: `${successRate}%`, icon: Percent, color: "bg-purple-100 text-purple-600" },
          { title: t ? t("avgDeposit") : "Avg Deposit", value: `฿${Number(avgDepositAmount).toLocaleString()}`, icon: ArrowDownToLine, color: "bg-sky-100 text-sky-600" },
          { title: t ? t("withdrawals") : "Withdrawals", value: `฿${totalWithdrawals.toLocaleString()}`, icon: ArrowUpFromLine, color: "bg-amber-100 text-amber-600" },
        ].map((stat, i) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center`}>
                <stat.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">{stat.title}</p>
                <p className="text-lg font-bold">{stat.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" className="gap-2"><Activity className="h-4 w-4" />{t ? t("overview") : "Overview"}</TabsTrigger>
          <TabsTrigger value="transactions" className="gap-2"><TrendingUp className="h-4 w-4" />{t ? t("transactions") : "Transactions"}</TabsTrigger>
          <TabsTrigger value="withdrawals" className="gap-2"><ArrowUpFromLine className="h-4 w-4" />{t ? t("withdrawals") : "Withdrawals"}</TabsTrigger>
          <TabsTrigger value="settings" className="gap-2"><Key className="h-4 w-4" />{t ? t("apiAndSettings") : "API & Settings"}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Volume Chart */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t ? t("volumeTrend7Days") : "Volume Trend (7 Days)"}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="depositGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="withdrawGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                        <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `${v}K`} />
                        <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "12px" }} />
                        <Area type="monotone" dataKey="deposits" name="Deposits" stroke="#3b82f6" strokeWidth={2} fill="url(#depositGrad)" />
                        <Area type="monotone" dataKey="withdrawals" name="Withdrawals" stroke="#10b981" strokeWidth={2} fill="url(#withdrawGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Quick Info */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card>
                <CardHeader>
                  <CardTitle>{t ? t("merchantInfo") : "Merchant Info"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">{t ? t("settlementPeriod") : "Settlement Period"}</p>
                    <p className="font-medium">{merchant.settlement_period || "T1"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t ? t("fee") : "Fee"}</p>
                    <p className="font-medium">{merchant.fee_value}{merchant.fee_type === "percentage" ? "%" : " THB"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t ? t("rateLimit") : "Rate Limit"}</p>
                    <p className="font-medium">{merchant.rate_limit || 100} req/min</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t ? t("features") : "Features"}</p>
                    <div className="flex gap-2 mt-1">
                      {merchant.deposit_enabled && <Badge variant="outline">{t ? t("deposit") : "Deposit"}</Badge>}
                      {merchant.withdrawal_enabled && <Badge variant="outline">{t ? t("withdrawal") : "Withdrawal"}</Badge>}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t ? t("limits") : "Limits"}</p>
                    <p className="text-sm">
                      {(t ? t("deposit") : "Deposit") + ": "}฿{merchant.min_deposit?.toLocaleString()} - ฿{merchant.max_deposit?.toLocaleString()}
                    </p>
                    <p className="text-sm">
                      {(t ? t("withdrawal") : "Withdrawal") + ": "}฿{merchant.min_withdrawal?.toLocaleString()} - ฿{merchant.max_withdrawal?.toLocaleString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Recent Transactions */}
            <div className="lg:col-span-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>{t ? t("recentTransactions") : "Recent Transactions"}</CardTitle>
                </CardHeader>
                <CardContent>
                  <AdminTransactionTable
                    transactions={combinedData.slice(0, 10)}
                    isLoading={txLoading || isLoadingQRCodes}
                    onView={setSelectedTx}
                    showMerchant={false}
                    showReseller={false}
                    compact
                    merchants={allMerchants}
                    resellers={resellers}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-4 border border-slate-100">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t ? t("searchIdRefMerchant") : "Search by ID or reference..."}
                  value={txFilters.search}
                  onChange={(e) => { setTxFilters({ ...txFilters, search: e.target.value }); setPage(0); }}
                  className="pl-10"
                />
              </div>
              <Select value={txFilters.type} onValueChange={(v) => { setTxFilters({ ...txFilters, type: v }); setPage(0); }}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t ? t("allTypes") : "All Types"}</SelectItem>
                  <SelectItem value="deposit">{t ? t("deposits") : "Deposits"}</SelectItem>
                  <SelectItem value="withdrawal">{t ? t("withdrawals") : "Withdrawals"}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={txFilters.status} onValueChange={(v) => { setTxFilters({ ...txFilters, status: v }); setPage(0); }}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t ? t("allStatus") : "All Status"}</SelectItem>
                  <SelectItem value="pending">{t ? t("pending") : "Pending"}</SelectItem>
                  <SelectItem value="processing">{t ? t("processing") : "Processing"}</SelectItem>
                  <SelectItem value="success">{t ? t("success") : "Success"}</SelectItem>
                  <SelectItem value="failed">{t ? t("failed") : "Failed"}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <AdminTransactionTable
              transactions={paginatedTxs}
              isLoading={txLoading || isLoadingQRCodes}
              onView={setSelectedTx}
              showMerchant={false}
              showReseller={false}
              merchants={allMerchants}
              resellers={resellers}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  {(t ? t("showing") : "Showing") +
                    ` ${page * pageSize + 1} ` +
                    (t ? t("to") : "to") +
                    ` ${Math.min((page + 1) * pageSize, filteredTxs.length)} ` +
                    (t ? t("of") : "of") +
                    ` ${filteredTxs.length}`}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    {(t ? t("page") : "Page") + ` ${page + 1} / ${totalPages}`}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Withdrawals Tab */}
        <TabsContent value="withdrawals">
          <div className="space-y-4">
            <AdminTransactionTable
              transactions={transactions.filter(t => t.type === "withdrawal")}
              isLoading={txLoading}
              onView={setSelectedTx}
              showMerchant={false}
              showReseller={false}
              merchants={allMerchants}
              resellers={resellers}
            />
          </div>
        </TabsContent>

        {/* API & Settings Tab */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* API Keys */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  {t ? t("apiCredentialsTitle") : "API Credentials"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 mb-2">{t ? t("apiKeyPublic") : "API Key"}</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm">
                      {showApiKey ? (merchant.api_key || (t ? t("nA") : "Not set")) : "••••••••••••••••"}
                    </code>
                    <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)}>
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => copyKey(merchant.api_key, "api")}>
                      {copiedKey === "api" ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-2">{t ? t("secretKey") : "Secret Key"}</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm">
                      {showSecretKey ? (merchant.secret_key || (t ? t("nA") : "Not set")) : "••••••••••••••••"}
                    </code>
                    <Button variant="outline" size="icon" onClick={() => setShowSecretKey(!showSecretKey)}>
                      {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => copyKey(merchant.secret_key, "secret")}>
                      {copiedKey === "secret" ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Webhook Config */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Webhook className="h-5 w-5" />
                  {t ? t("webhookInfo") : "Webhook Configuration"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 mb-2">{t ? t("callbackUrlLabel") : "Callback URL"}</p>
                  <code className="block bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm break-all">
                    {merchant.callback_url || (t ? t("nA") : "Not configured")}
                  </code>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-2">{t ? t("webhookUrl") : "Webhook URL"}</p>
                  <code className="block bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm break-all">
                    {merchant.webhook_url || (t ? t("nA") : "Not configured")}
                  </code>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-2">{t ? t("ipWhitelist") : "IP Whitelist"}</p>
                  <div className="flex flex-wrap gap-2">
                    {(merchant.ip_whitelist || []).length > 0 ? (
                      merchant.ip_whitelist.map((ip, i) => (
                        <Badge key={i} variant="outline" className="font-mono">{ip}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400">
                        {t ? t("noIpsWhitelisted") : "No IPs whitelisted"}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        engine={parentEngine}
        reseller={parentReseller}
        merchant={merchant}
        isQRCode={!!selectedTx?.is_qr_code}
      />
    </div>
  );
}